'use client'

import { useContext, useMemo } from 'react'
import useCurrentLocale from '../../../../core/hook/useCurrentLocale'
import { ConfigContext, useTranslation } from '../../../context/ConfigContext'
import { getIsFranceLocale } from '../../../utils/locale.utils'
import { localeDate } from '../../../utils/date.utils'
import Holidays from 'date-holidays'

export const ProductConfigurationMaybeOnStore = () => {
  const t = useTranslation()
  return (
    <p className='product-configuration-maybeonstore-wording'>
      {t(
        'This is piece is out of stock on our website, but may be available in our network. Please contact us for more information.',
        {},
        true,
      )}
    </p>
  )
}

const formatDateLocal = (local) => {
  switch (local) {
    case 'us_en':
      return 'US'
    default:
      return 'FR'
  }
}

const useCalculateDeliveryDate = (isCarving = false) => {
  const { storeConfig } = useContext(ConfigContext)
  const currentLocale = useCurrentLocale()
  const holidays = new Holidays()
  holidays.init(formatDateLocal(currentLocale))
  const allHollidays = holidays.getHolidays(new Date().getFullYear())

  const deliveryDate = useMemo((): Date => {
    const datesAvailableForShipping: Date[] = []

    const classicShiping = +storeConfig?.shipping_shipping_policy_delay
    const carvingShiping =
      +storeConfig?.catalog_engraving_additional_shipping_delay + classicShiping
    const numberOfDaysForDelivery = isCarving ? carvingShiping : classicShiping

    let incrementedDate = new Date()

    while (datesAvailableForShipping.length < numberOfDaysForDelivery) {
      const deliveryDateIncremented = new Date(incrementedDate)
      deliveryDateIncremented.setDate(incrementedDate.getDate() + 1)

      const day = deliveryDateIncremented.getDate().toString().padStart(2, '0') // Get day and pad with leading zero if needed
      const month = (deliveryDateIncremented.getMonth() + 1).toString().padStart(2, '0') // Get month (0-indexed) and pad with leading zero
      const year = deliveryDateIncremented.getFullYear()

      const finded = allHollidays.find(
        (holiday) => holiday.date === `${year}-${month}-${day} 00:00:00`,
      )

      if (!finded && ![0, 6].includes(deliveryDateIncremented.getDay())) {
        datesAvailableForShipping.push(deliveryDateIncremented)
      }

      incrementedDate = deliveryDateIncremented
    }

    return datesAvailableForShipping[datesAvailableForShipping.length - 1]
  }, [
    storeConfig?.shipping_shipping_policy_delay,
    storeConfig?.catalog_engraving_additional_shipping_delay,
    currentLocale,
    isCarving,
  ])

  return deliveryDate
}

type ProductConfigurationDeliveryWordingProps = {
  isCarving?: boolean
}

export const ProductConfigurationDeliveryWording = ({
  isCarving = false,
}: ProductConfigurationDeliveryWordingProps) => {
  const deliveryDate = useCalculateDeliveryDate(isCarving)
  const t = useTranslation()
  const currentLocale = useCurrentLocale()
  const isFranceLocale = getIsFranceLocale(currentLocale)

  const showWhiteGlovesWording = isFranceLocale && !isCarving
  const { isEcommerce } = useContext(ConfigContext)

  return (
    <>
      {isEcommerce() && (
        <div className='shipping-container'>
          <p className='shipping'>
            {t('catalog-product-shipping-estimation')}{' '}
            {localeDate(deliveryDate, currentLocale)}
          </p>
          {showWhiteGlovesWording && (
            <p className='shipping'>{t('catalog-product-shipping-gb')}</p>
          )}
        </div>
      )}
    </>
  )
}
